import { useAuth0 } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { LandingPage } from '@/pages/public/LandingPage';

/**
 * Shows the public landing page at `/` to somebody who is NOT signed in,
 * instead of sending them straight to Auth0. Everything else is unchanged:
 * signed-in users get the portal at `/`, and every other protected path still
 * goes straight to the login.
 *
 * <p>
 * `/` HAS TO BE PUBLIC because it is the home page registered with Microsoft
 * and Google. Their reviewers — and a customer's IT admin deciding whether to
 * approve the app — open it without an account; a login screen there fails the
 * verification.
 * </p>
 *
 * <p>
 * Sits OUTSIDE RequireAuth, so it decides before RequireAuth's effect can start
 * a login redirect.
 * </p>
 */
export function LandingWhenSignedOut({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const { pathname, search } = useLocation();

  if (pathname !== '/') return <>{children}</>;

  // Auth0 sends the browser back to `/?code=…&state=…` after login. That has
  // to reach the provider untouched, or the session never completes.
  const params = new URLSearchParams(search);
  if (params.has('code') || params.has('error')) return <>{children}</>;

  // Auth0 is still checking for an existing session. A blank dark page, not
  // RequireAuth's "Signing in…" — most people at `/` are not signing in.
  if (isLoading) return <div className="min-h-full bg-slate-950" />;

  // An error (e.g. access_denied) is RequireAuth's to explain.
  if (!isAuthenticated && !error) return <LandingPage />;

  return <>{children}</>;
}
